/**
 * Features a group can have, or not.
 *
 * Lives in `@esa/shared` rather than `@esa/core` because the web bundle needs
 * it — the console hides what a group does not have — and `@esa/core` pulls
 * `node:fs/promises` into the browser, which blanks the entire SPA while
 * typechecking clean. That has happened once; the structural guard at
 * `packages/web/src/lib/imports.test.ts` exists because of it.
 *
 * TICKETING IS NOT HERE ON PURPOSE. A group with ticketing switched off is
 * not a configuration, it is a deleted tenant, and `ticket.*` capabilities
 * are never module-gated. Everything else is optional to somebody.
 */

export const MODULES = [
  'knowledge',
  'reports',
  'daylight',
  'mining',
  'security',
  'webhooks',
  'portal',
  'intake',
  'assistant',
  'actions',
  'reply_suggestions',
] as const;

export type ModuleKey = (typeof MODULES)[number];

/**
 * `off` is not "hidden": a route belonging to an off module answers 404.
 *
 * 404 rather than 403 because 403 says "this exists and you may not have it",
 * which is a different and usually untrue statement. For a group that does
 * not have security campaigns, the campaigns endpoint genuinely is not part
 * of their system.
 */
export type ModuleState = 'off' | 'pilot' | 'on';

/**
 * What each module needs in order to do anything.
 *
 * Deliberately sparse. A dependency here is a hard one -- the feature is
 * inert without it, not merely less useful -- because every edge is a
 * combination a platform admin can no longer choose, and inventing edges
 * costs them control over their own rollout.
 *
 * `daylight`, `reports`, `security`, `webhooks`, `portal` and `intake` are
 * all arithmetic or plumbing over tickets and depend on nothing. `knowledge`
 * depends on nothing either: articles can be written by hand, so it does not
 * need the miner to be useful.
 */
export const MODULE_REQUIRES: Readonly<Partial<Record<ModuleKey, readonly ModuleKey[]>>> = {
  // Mining writes `kb_patterns`, which only the knowledge screens read and
  // only `draftArticle` turns into anything. With knowledge off the miner
  // still runs and still charges per run, and produces rows nobody can open.
  mining: ['knowledge'],
  // The assistant answers from published articles. With knowledge off there
  // is nothing published to answer from, so it would quietly file every
  // request as a ticket while reporting itself as enabled.
  assistant: ['knowledge'],
};

/**
 * Prerequisites that are not met, given what is available.
 *
 * Transitive: a chain A -> B -> C resolves in one pass here because the
 * caller walks modules in order and consults the already-resolved set. There
 * is no cycle in the map above and a cycle would be a bug, not a
 * configuration, so this does not defend against one.
 */
export function unmetRequirements(
  module: ModuleKey,
  availableSoFar: ReadonlySet<ModuleKey>,
): ModuleKey[] {
  return (MODULE_REQUIRES[module] ?? []).filter((r) => !availableSoFar.has(r));
}

/** Which modules something else needs. */
export function dependentsOf(module: ModuleKey): ModuleKey[] {
  return MODULES.filter((k) => (MODULE_REQUIRES[k] ?? []).includes(module));
}

export interface ModuleMeta {
  key: ModuleKey;
  label: string;
  /**
   * What a NEW group gets.
   *
   * Every module is 'on' so a new desk behaves like the product — except
   * `actions`, which changes systems outside this one. Nobody has agreed to
   * that by creating a group, and a capability that did not exist yesterday
   * should not arrive switched on.
   */
  defaultState?: 'on' | 'off';
  /** What a platform admin is deciding about, in their words not ours. */
  description: string;
  /** What actually disappears when this is off. */
  affects: string;
}

export const MODULE_META: Readonly<Record<ModuleKey, ModuleMeta>> = {
  knowledge: {
    key: 'knowledge',
    label: 'Knowledge',
    description: 'Written-up answers, the review cycle, and suggestions on tickets.',
    affects: 'The Suggestions screen, and the Known solutions panel on a ticket.',
  },
  reports: {
    key: 'reports',
    label: 'Reporting',
    description: 'The dashboard and desk performance reports.',
    affects: 'Dashboard and Desk performance in the sidebar, and every CSV export.',
  },
  daylight: {
    key: 'daylight',
    label: 'Stalls & automation',
    description: 'Why open work is not moving, and which repeated requests are worth automating.',
    affects: 'The Stalls & automation screen.',
  },
  mining: {
    key: 'mining',
    label: 'Pattern mining',
    description:
      'Reading resolved tickets to find problems the desk keeps solving. Costs money per run.',
    affects: 'Mined candidates on the Suggestions screen. Articles already written stay.',
    /**
     * OFF FOR A NEW GROUP, on the same reasoning as `actions`: something
     * that spends money is not acquired by existing. Mining reads a desk's
     * whole resolved history through a model on its first run, so a group
     * created on a Friday should not produce a bill by Monday because
     * nobody said otherwise.
     *
     * This default is what lets the switch below be the ONLY switch. While
     * mining defaulted on, a deployment-wide `KB_MINING_ENABLED` had to
     * exist to stop every new group spending — two controls for one
     * decision, and two places to look when mining "is not running".
     */
    defaultState: 'off',
  },
  security: {
    key: 'security',
    label: 'Security campaigns',
    description: 'Correlating reported phishing into one campaign rather than many tickets.',
    affects: 'Campaign detection and the campaign views.',
  },
  webhooks: {
    key: 'webhooks',
    label: 'Webhooks',
    description: 'Outbound notifications to other systems when a ticket changes.',
    affects: 'The Webhooks tab in Configuration, and all outbound delivery.',
  },
  portal: {
    key: 'portal',
    label: 'Requester portal',
    description: 'Self-service: people raising and following their own requests.',
    affects: 'My requests and New request. Tickets still arrive by email.',
  },
  intake: {
    key: 'intake',
    label: 'Intake',
    description: 'The email intake screen, replay and suppression.',
    affects: 'The Intake screen. Mail is still received and routed.',
  },
  actions: {
    key: 'actions',
    label: 'Actions',
    description:
      'Letting an authorized agent make real changes in other systems from a ticket — granting Egnyte folder access, for example.',
    affects:
      'The Actions panel on a ticket. Nothing runs without a person authorizing that exact change.',
    defaultState: 'off',
  },
  assistant: {
    key: 'assistant',
    label: 'Assistant answers',
    description:
      'Letting the assistant answer a request from published knowledge instead of filing it.',
    affects: 'Auto-responses. Requests still become tickets when it cannot answer.',
  },
  reply_suggestions: {
    key: 'reply_suggestions',
    label: 'Suggested replies',
    description:
      "Letting an agent ask for a suggested reply, written in their own style from the replies they have sent before, the ticket, and the knowledgebase.",
    affects: 'The Suggest a reply button in the composer. Nothing is sent without the agent sending it.',
  },
};

/**
 * Has this group got the module at all, for this person?
 *
 * This answers the STATE question only. A module whose prerequisite is
 * missing is handled by `resolveModules` below, which is what callers should
 * use -- this is the per-module half of it.
 */
export function moduleAvailable(
  state: ModuleState | undefined,
  isPilot: boolean,
): boolean {
  if (state === 'on') return true;
  if (state === 'pilot') return isPilot;
  // Absent is off. A module nobody has ever considered for this group is not
  // one they have — defaulting the other way would switch a half-built
  // feature on for six desks the moment it was named.
  return false;
}

/**
 * What is actually in force, once prerequisites are taken into account.
 *
 * Returns the set of modules that both the group has (for this person) AND
 * whose prerequisites are themselves in force.
 *
 * Resolved to a FIXED POINT rather than in one pass. A single pass over
 * `MODULES` happens to work today only because `knowledge` is declared
 * before the two things that need it — which means reordering that array,
 * an edit with no obvious connection to dependencies, would silently start
 * suppressing modules that should be available. Looping until nothing
 * changes costs a handful of iterations over nine items and removes the
 * trap entirely.
 *
 * The suppressed list is returned rather than discarded because the console
 * has to be able to say WHY something is unavailable. "Mining is on but does
 * nothing" is the state this whole map exists to make visible; hiding the
 * reason would replace one silent incoherence with another.
 */
export function resolveModules(
  chosen: (m: ModuleKey) => boolean,
): { available: ModuleKey[]; suppressed: { module: ModuleKey; missing: ModuleKey[] }[] } {
  const wanted = MODULES.filter(chosen);
  const available = new Set<ModuleKey>();

  for (;;) {
    const added = wanted.filter(
      (m) => !available.has(m) && unmetRequirements(m, available).length === 0,
    );
    if (added.length === 0) break;
    for (const m of added) available.add(m);
  }

  // Whatever is still outstanding is genuinely blocked, not merely unvisited.
  const suppressed = wanted
    .filter((m) => !available.has(m))
    .map((m) => ({ module: m, missing: unmetRequirements(m, available) }));

  return { available: MODULES.filter((m) => available.has(m)), suppressed };
}
